import { useCallback } from "react"
import { useMutation } from "urql"

import type { MaterialRequirementsFormValues } from "../types"

import type {
  LocationMaterialRequirementsPreferenceInput,
  UpsertLocationMaterialRequirementsPreferenceMutation,
} from "@/graphql/codegen/graphql"
import { UpsertLocationMaterialRequirementsPreferenceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SaveMaterialRequirementsInput {
  locationId: string
  formValues: MaterialRequirementsFormValues
}

export interface UseSaveMaterialRequirementsReturn {
  saveMaterialRequirements: (input: SaveMaterialRequirementsInput) => Promise<boolean>
  isSaving: boolean
}

export const useSaveMaterialRequirements = (): UseSaveMaterialRequirementsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    UpsertLocationMaterialRequirementsPreferenceMutation,
    { input: LocationMaterialRequirementsPreferenceInput }
  >(UpsertLocationMaterialRequirementsPreferenceDocument)

  const saveMaterialRequirements = useCallback(
    async ({ locationId, formValues }: SaveMaterialRequirementsInput): Promise<boolean> => {
      const input: LocationMaterialRequirementsPreferenceInput = {
        shipToLocationId: locationId,
        allowsRecertifiedMaterial: formValues.allowsRecertifiedMaterial,
        prefersNewestMaterial: formValues.prefersNewestMaterial,
        shelfLifeRequired: formValues.shelfLifeRequired,
        shelfLifeRequirement: formValues.shelfLifeRequirement ?? null,
        changeApprovalRequired: formValues.changeApprovalRequired,
        prefersFewestLots: formValues.prefersFewestLots,
        labelConfiguration: formValues.labelConfiguration,
      }

      const { data, error } = await executeMutation({ input })
      const output = data?.locationMaterialRequirementsPreference.upsert

      const hasError = error || output?.__typename === "LocationMaterialRequirementsPreferenceUpsertFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "LocationMaterialRequirementsPreferenceUpsertFailure"
            ? output.error.message
            : error?.message

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage ?? "Failed to save material requirements. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Material requirements saved successfully.",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { saveMaterialRequirements, isSaving: fetching }
}
